import { useReducer } from 'react';
import { useMutation } from '@apollo/react-hooks';
import { captureException } from '@sentry/react';
import createEmailSubscriptionMutation from 'graphql/createEmailSubscriptionMutation.graphql';

const BLOG_MARKETING_CAMPAIGN_LIST = 'iterablezapierblog';
const LOADING = 'LOADING';
const ERROR = 'ERROR';
const SUCCESS = 'SUCCESS';

type InitialState = {
  loading: boolean;
  error: boolean;
  success: boolean;
};

type Action = { type: typeof LOADING | typeof ERROR | typeof SUCCESS };

// When submitEmail runs, there are a number of ways we might need to update the state depending on how the promise is resolved. This function will update each piece of state in one go instead of individually.
function reducer(state: InitialState, action: Action) {
  switch (action.type) {
    case LOADING:
      return { loading: true, error: false, success: false };
    case ERROR:
      return { loading: false, error: true, success: false };
    case SUCCESS:
      return { loading: false, error: false, success: true };
    default:
      return state;
  }
}

export const useSubscribeEmail = () => {
  // Define the initial state of the form's state so the reducer can update it
  const initialState = {
    loading: false,
    error: false,
    success: false,
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const [createEmailSubscription] = useMutation(
    createEmailSubscriptionMutation,
    {
      errorPolicy: 'none',
    },
  );

  const submitEmail = async (email: string) => {
    try {
      dispatch({ type: LOADING });
      if (email) {
        await createEmailSubscription({
          variables: { email, channel: BLOG_MARKETING_CAMPAIGN_LIST },
        });
        dispatch({ type: SUCCESS });
      }
    } catch {
      dispatch({ type: ERROR });
      captureException(`Error subscribing ${email} to the newsletter`);
    }
  };

  return { ...state, submitEmail };
};
